import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  generateGiftOtpRequest,
  verifyGiftOtpRequest,
  resetGiftState,
} from "../../redux/Gifts/giftSlice";
import {
  getHoldingsRequest,
  getBullionRatesRequest,
  getAppConfigRequest,
  getCarouselRequest,
  getSipBalanceRequest,
  getKycDetailsRequest,
} from "../../redux/Dashboard/dashboardSlice";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import CarouselSection from "../CarouselSection/CarouselSlider";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const GiftScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { giftCreated } = useSelector((state) => state.gift);

  const {
    data: holdings,
    bullionRates,
    carousel,
    appConfig,
  } = useSelector((state) => state.dashboard || {});
  const { isLoading, isOtpSent, successMessage } = useSelector(
    (state) => state.gift || {},
  );
  const decimal = appConfig?.decimalPrecision ?? 4;

  const [selectedMetal, setSelectedMetal] = useState(null);
  const [grams, setGrams] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpScreen, setIsOtpScreen] = useState(false);
  const [timer, setTimer] = useState(60);
  const timerRef = useRef(null);
  const [isResendAvailable, setIsResendAvailable] = useState(false);

  const [topCarousel, setTopCarousel] = React.useState(null);
  const [midCarousel, setMidCarousel] = React.useState(null);
  const [bottomCarousel, setBottomCarousel] = React.useState(null);

  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  useEffect(() => {
    if (giftCreated) {
      navigate("/GiftHistory");
      dispatch(resetGiftState());
    }
  }, [giftCreated, navigate, dispatch]);

  useEffect(() => {
    dispatch(
      getHoldingsRequest({ jeweller: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(
      getBullionRatesRequest({ id: `${process.env.REACT_APP_JEWELLER_ID}` }),
    );
    dispatch(getAppConfigRequest());
    dispatch(getCarouselRequest());
    dispatch(getSipBalanceRequest());
    dispatch(getKycDetailsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (isOtpSent) {
      setIsOtpScreen(true);
      startTimer();
    }
  }, [isOtpSent]);

  const startTimer = () => {
    clearInterval(timerRef.current);
    setTimer(60);
    setIsResendAvailable(false);

    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setIsResendAvailable(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => clearInterval(timerRef.current);
  }, []);

  const normalizeKarat = (karat) => {
    if (!karat) return "";
    return String(karat)
      .toLowerCase()
      .replace(/[^0-9]/g, ""); // keep only numbers
  };

  const filteredMetals = useMemo(() => {
    if (!bullionRates) return [];
    console.log("bullionRates", bullionRates);
    const metals = bullionRates
      .filter((r) => r.bullion?.metalName?.toLowerCase() !== "money")
      .map((r) => ({
        metalName: r.bullion.metalName,
        purity: r.bullion.purity,
        karat: r.bullion.karat,
        currentBuyRate: r.ratePerGram || 0,
      }));
    const uniqueMetals = metals.filter(
      (metal, index, self) =>
        index ===
        self.findIndex(
          (m) =>
            m.metalName === metal.metalName &&
            m.karat === metal.karat &&
            m.purity === metal.purity,
        ),
    );
    return uniqueMetals;
  }, [bullionRates]);

  useEffect(() => {
    if (!selectedMetal && filteredMetals.length > 0) {
      setSelectedMetal(filteredMetals[0]);
    }
  }, [filteredMetals, selectedMetal]);

  const currentHolding = useMemo(() => {
    if (!holdings || !selectedMetal) return null;

    const norm = (v) => (v == null ? "" : String(v).trim().toLowerCase());

    const metalHoldings = holdings.filter((h) => {
      return (
        norm(h.metalName) === norm(selectedMetal.metalName) &&
        normalizeKarat(h.karat) === normalizeKarat(selectedMetal.karat) &&
        String(h.purity) === String(selectedMetal.purity)
      );
    });

    if (metalHoldings.length === 0) return null;

    const totalAvailable = metalHoldings.reduce(
      (sum, h) => sum + (Number(h.availableBalance) || 0),
      0,
    );
    const totalGift = metalHoldings.reduce(
      (sum, h) => sum + (Number(h.giftBalance) || 0),
      0,
    );

    const netGrams = totalAvailable - totalGift;
    const totalValue = netGrams * (selectedMetal.currentBuyRate || 0);

    return {
      grams: netGrams.toFixed(decimal),
      value: totalValue.toFixed(decimal),
    };
  }, [holdings, selectedMetal]);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      let top = {},
        mid = {},
        bottom = {};

      carousel.data.forEach((e) => {
        switch (e.name) {
          case "gift_main_top":
            top = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "gift_main_middle":
            mid = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "gift_main_bottom":
            bottom = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          default:
            break;
        }
      });

      setTopCarousel(top);
      setMidCarousel(mid);
      setBottomCarousel(bottom);
    } else {
      setTopCarousel(null);
      setMidCarousel(null);
      setBottomCarousel(null);
    }
  }, [carousel]);

  const handleGenerateOtp = () => {
    if (!grams) {
      toast.error("Please enter valid grams");
      return;
    }
    if (Number(grams) <= 0) {
      toast.error("Invalid weight entered");
      return;
    }
    if (!phoneNumber) {
      toast.error("Please enter phone number");
      return;
    }
    if (!message) {
      toast.error("Please enter a message");
      return;
    }

    const giftData = {
      metalName: selectedMetal.metalName,
      purity: selectedMetal.purity,
      karat: selectedMetal.karat,
      weight: parseFloat(grams),
      giftTo: phoneNumber,
      occasion: message,
    };

    dispatch(generateGiftOtpRequest({ phoneNumber, giftData }));
  };

  const handleVerifyOtp = () => {
    if (!grams || Number(grams) <= 0) {
      toast.error("Invalid weight entered");
      return;
    }
    if (!otp) {
      toast.error("Enter OTP");
      return;
    }

    const giftData = {
      metalName: selectedMetal.metalName,
      purity: selectedMetal.purity,
      karat: selectedMetal.karat,
      weight: parseFloat(grams),
      giftTo: phoneNumber,
      occasion: message,
    };

    dispatch(verifyGiftOtpRequest({ otp, phoneNumber, giftData }));
  };

  const handleResendOtp = () => {
    if (!grams || Number(grams) <= 0) {
      toast.error("Invalid weight entered");
      return;
    }
    if (!phoneNumber) return;

    const giftData = {
      metalName: selectedMetal.metalName,
      purity: selectedMetal.purity,
      karat: selectedMetal.karat,
      weight: parseFloat(grams),
      giftTo: phoneNumber,
      occasion: message,
    };

    dispatch(generateGiftOtpRequest({ phoneNumber, giftData }));
    startTimer();
    toast.info("OTP resent successfully");
  };

  const handleBackToForm = () => {
    setIsOtpScreen(false);
    clearInterval(timerRef.current);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        px: 2,
        py: 4,
        background: themeColors.scaffoldGradientColors,
      }}
    >
      <Card
        sx={{
          maxWidth: 900,
          width: "100%",
          borderRadius: 3,
          boxShadow: 8,
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 5 } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              align="center"
              sx={{
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
                background: themeColors.primary,
                backgroundClip: "text",
                textFillColor: "transparent",
              }}
            >
              Gift Metal
            </Typography>

            {!isOtpScreen && (
              <Button
                variant="outlined"
                onClick={() => {
                  navigate("/GiftHistory");
                }}
                sx={{
                  border: `1px solid ${themeColors.scaffold}`,
                  color: themeColors.text,
                  fontWeight: 500,
                  textTransform: "none",
                  borderRadius: 2,
                  "&:hover": {
                    borderColor: "#7028DA",
                    background: "rgba(112,40,218,0.1)",
                  },
                }}
              >
                Gift History
              </Button>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {topCarousel && (
            <CarouselSection
              slides={topCarousel.slides}
              height={topCarousel.height}
              showNavigator={topCarousel.showNavigator}
              duration={topCarousel.duration}
              enabled={topCarousel.enabled}
            />
          )}

          {!isOtpScreen ? (
            <>
              {/* Metal Selection – Pill UI */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    background: "#0B2C4A",
                    borderRadius: "40px",
                    padding: "6px",
                    gap: "6px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  {filteredMetals.map((metal, index) => {
                    const { metalName, purity, karat } = metal;

                    const isSelected =
                      selectedMetal?.metalName === metalName &&
                      selectedMetal?.karat === karat &&
                      selectedMetal?.purity === purity;

                    return (
                      <Box
                        key={index}
                        onClick={() => setSelectedMetal(metal)}
                        sx={{
                          px: 3,
                          py: 1.2,
                          borderRadius: "30px",
                          cursor: "pointer",
                          minWidth: 120,
                          textAlign: "center",
                          transition: "all 0.25s ease",
                          background: isSelected
                            ? themeColors.primary
                            : "transparent",
                          border: `1px solid ${themeColors.scaffold}`,
                          boxShadow: isSelected ? themeColors.border : "none",
                          color: themeColors.text,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          {metalName}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "11px",
                            opacity: 0.8,
                          }}
                        >
                          {karat}K – {purity}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              {/* Selected Metal Display */}
              {selectedMetal && (
                <Box sx={{ mb: 3, px: 2 }}>
                  <Box
                    sx={{
                      height: 60,
                      borderRadius: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      color: "#000",
                      background:
                        selectedMetal.metalName?.toLowerCase() === "gold"
                          ? "linear-gradient(135deg, #FFD700, #DAA520)"
                          : "linear-gradient(135deg, #C0C0C0, #E0E0E0)",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
                        {selectedMetal.metalName?.toUpperCase()}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "13px", fontWeight: 500, opacity: 0.9 }}
                      >
                        {selectedMetal.karat}K – {selectedMetal.purity}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {currentHolding ? (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    background: themeColors.bigContainer,
                    border: `1px solid ${themeColors.scaffold}`,
                    color: themeColors.text,
                  }}
                >
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      mb={1}
                      align="center"
                    >
                      Current Balance
                    </Typography>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid size={{ xs: 6 }} textAlign="center">
                        <Typography variant="body2">Grams</Typography>
                        <Typography variant="h6">
                          {currentHolding.grams}
                        </Typography>
                      </Grid>
                      {/* <Grid size={{ xs: 6 }} textAlign="center">
                        <Typography variant="body2">Value ({symbol})</Typography>
                        <Typography variant="h6">
                          {currentHolding.value}
                        </Typography>
                      </Grid> */}
                    </Grid>
                  </CardContent>
                </Card>
              ) : (
                <Box textAlign="center" mb={3}>
                  <CircularProgress size={28} sx={{ color: "#9E3ED7" }} />
                  <Typography variant="body2" mt={1}>
                    Loading current balance...
                  </Typography>
                </Box>
              )}

              {midCarousel && (
                <CarouselSection
                  slides={midCarousel.slides}
                  height={midCarousel.height}
                  showNavigator={midCarousel.showNavigator}
                  duration={midCarousel.duration}
                  enabled={midCarousel.enabled}
                />
              )}

              <Card
                variant="outlined"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  background: themeColors.bigContainer,
                  border: `1px solid ${themeColors.scaffold}`,
                  color: themeColors.text,
                }}
              >
                <CardContent>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    mb={2}
                    align="center"
                    sx={{ color: themeColors.text }}
                  >
                    Gift {selectedMetal?.metalName}
                  </Typography>

                  <Grid container direction="column" spacing={2}>
                    <Grid>
                      <TextField
                        fullWidth
                        label="Enter Grams"
                        value={grams}
                        onChange={(e) => {
                          const g = e.target.value;
                          const gramDecimalRegex = new RegExp(
                            `^\\d*(\\.\\d{0,${decimal}})?$`,
                          );
                          if (g === "") {
                            setGrams("");
                            return;
                          }
                          if (!gramDecimalRegex.test(g)) return;
                          if (
                            currentHolding &&
                            Number(g) > Number(currentHolding.grams)
                          ) {
                            setGrams(String(currentHolding.grams));
                            toast.info(
                              `Cannot enter more than ${currentHolding.grams} grams`,
                              { toastId: "gift-grams-limit" },
                            );
                            return;
                          }
                          setGrams(g);
                        }}
                        type="number"
                        inputProps={{ min: 0 }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: themeColors.mediumContainer,
                            color: themeColors.text,
                          },
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& .MuiInputLabel-root": {
                            color: themeColors.text,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.scaffold,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                        }}
                      />
                    </Grid>
                    <Grid>
                      <TextField
                        fullWidth
                        label="Phone Number"
                        value={phoneNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*$/.test(value)) {
                            setPhoneNumber(value);
                          }
                        }}
                        type="tel"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: themeColors.mediumContainer,
                            color: themeColors.text,
                          },
                          "& .MuiInputLabel-root": {
                            color: themeColors.text,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.scaffold,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                        }}
                      />
                    </Grid>
                    <Grid>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Enter Message"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            background: themeColors.mediumContainer,
                            color: themeColors.text,
                          },
                          "& .MuiInputLabel-root": {
                            color: themeColors.text,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.scaffold,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: themeColors.primary,
                          },
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {bottomCarousel && (
                <CarouselSection
                  slides={bottomCarousel.slides}
                  height={bottomCarousel.height}
                  showNavigator={bottomCarousel.showNavigator}
                  duration={bottomCarousel.duration}
                  enabled={bottomCarousel.enabled}
                />
              )}

              {/* Generate OTP Button */}
              <Box display="flex" justifyContent="center">
                <Button
                  variant="contained"
                  onClick={handleGenerateOtp}
                  sx={{
                    background: themeColors.primary,
                    color: themeColors.text,
                    "&:hover": {
                      background: themeColors.svgColor,
                    },
                    border: `1px solid ${themeColors.scaffold}`,
                    fontWeight: 500,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                    px: 5,
                    py: 1.2,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  Generate OTP
                </Button>
              </Box>
            </>
          ) : (
            <Box textAlign="center">
              <Typography variant="h6" mb={2}>
                Enter the OTP sent to {phoneNumber}
              </Typography>

              <TextField
                label="Enter OTP"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                fullWidth
                autoFocus
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    background: themeColors.mediumContainer,
                    color: themeColors.text,
                  },
                  "& .MuiInputLabel-root": {
                    color: themeColors.text,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeColors.scaffold,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeColors.primary,
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: themeColors.primary,
                  },
                }}
              />

              {isResendAvailable ? (
                <Typography
                  onClick={handleResendOtp}
                  sx={{
                    color: themeColors.primary,
                    cursor: "pointer",
                    textDecoration: "underline",
                    mb: 3,
                  }}
                >
                  Resend OTP
                </Typography>
              ) : (
                <Typography mb={3}>Resend OTP in {timer}s</Typography>
              )}

              <Box display="flex" justifyContent="center" gap={2}>
                <Button
                  variant="outlined"
                  onClick={handleBackToForm}
                  sx={{
                    borderRadius: 2,
                    background: themeColors.primary,
                    color: themeColors.text,
                    "&:hover": {
                      background: themeColors.svgColor,
                    },
                    border: `1px solid ${themeColors.scaffold}`,
                  }}
                >
                  Back
                </Button>

                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={isLoading}
                  sx={{
                    background: themeColors.primary,
                    color: themeColors.text,
                    "&:hover": {
                      background: themeColors.svgColor,
                    },
                    border: `1px solid ${themeColors.scaffold}`,
                    fontWeight: 500,
                    px: 5,
                    py: 1.2,
                    textTransform: "none",
                    borderRadius: 2,
                  }}
                >
                  {isLoading ? "Verifying..." : "Verify OTP"}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default GiftScreen;
